<script setup>
import { Button } from '@dashhudson/dashing-ui/button';
import { EditableSelect } from '@dashhudson/dashing-ui/editable-select';
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { computed, ref, toRefs } from 'vue';
import { logger } from '@/utils/logger';
import { useMediaDetailStore } from '@/stores/media-detail';
import { useAuthStore } from '@/stores/auth';

const mediaDetailStore = useMediaDetailStore();
const authStore = useAuthStore();
const { addToast } = useToast({
  life: 5000,
});

const props = defineProps({
  selectedMedia: { type: Array, required: true },
  disabled: { type: Boolean, default: false },
});

const { selectedMedia } = toRefs(props);

const selectedMediaWithoutDMedStories = computed(() =>
  selectedMedia.value.filter((media) => !media?.instagramStoryFrame?.importedViaDm),
);

const emit = defineEmits(['onActionComplete']);

const visualTags = ref([]);

function onOpenDropdown() {
  visualTags.value = [];
}

function onAddNew(tagName) {
  if (visualTags.value.map((option) => option?.value).includes(tagName)) {
    throw new Error('Visual tag already exists');
  }

  return {
    label: tagName,
    value: tagName,
    readonly: true,
  };
}

function onRemoveOption(option) {
  visualTags.value = visualTags.value.filter((tag) => tag?.value !== option.value);
}

async function onBulkAddTags(tags) {
  const mediaIds = selectedMediaWithoutDMedStories.value?.map(
    (item) => (item.media && item.media.media_id) || item.id,
  );
  const brandId = authStore.currentBrand.id;

  try {
    await mediaDetailStore.addMultipleMediaTags({ brandId, mediaIds, tags });
    addToast({
      severity: 'success',
      message: `Your visual tag${visualTags.value.length > 1 ? 's have' : ' has'} been added!`,
    });
  } catch (e) {
    addToast({
      severity: 'error',
      message: 'Something went wrong. Please try again later!',
    });
    logger.error('Failed to add visual tags', {}, e);
  }

  emit('onActionComplete');
}
</script>

<template>
  <EditableSelect
    v-model:options="visualTags"
    add-new-placeholder="Enter a visual tag"
    :empty-text="null"
    :add-new-callback="onAddNew"
    :dropdown="{
      title: 'Visual Tags',
      placement: 'bottom-end',
    }"
    :remove-callback="onRemoveOption"
    disable-select-all
    @update:model-value="onBulkAddTags"
  >
    <Button
      v-dash-tooltip="`Add Visual Tags`"
      size="xs"
      severity="bulk"
      icon="magic-wand"
      :disabled="props.disabled"
      @click="onOpenDropdown"
    />
  </EditableSelect>
</template>
